import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Modal,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { useMemo, useState } from 'react';
import { useTimeZone } from '@/components';
import { formatTimeRangeInputValue } from '@/components/DateTimeRangePicker/utils';
import { SAVE_CHANGES_WITH_SAVED_VIEWS_TEXT, SAVE_DEFAULT_TIME_RANGE_TEXT, SAVE_DEFAULTS_DIALOG_TEXT } from '@/core';
import { SAVED_VIEW_EVENTS, SAVED_VIEW_SOURCE, savedViewPayload } from '@/dashboards/utils/saved-view-analytics';
import { generateDefaultNameFromValues, isDuplicateViewName } from '@/dashboards/utils/saved-view-utils';
import { useAnalytics, useTimeRange } from '@/plugin-system';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import {
  useDashboardMetadata,
  useDefaultViewName,
  useSaveChangesConfirmationDialog,
  useSavedViews,
  useTemplateVariableActions,
  useTemplateVariableViewState,
} from '../../context';

enum SavedViewAction {
  Skip = 'Skip',
  UpdateDefault = 'UpdateDefault',
  CreateNew = 'CreateNew',
}

export const SaveChangesConfirmationDialog = () => {
  const analytics = useAnalytics();
  const { enableDashboardSavedViews } = useFeatureFlags();
  const { metadata } = useDashboardMetadata();
  const analyticsPayload = useMemo(() => ({ dashboardSlug: metadata.slug }), [metadata.slug]);
  const { saveChangesConfirmationDialog: dialog } = useSaveChangesConfirmationDialog();
  const isSavedTimeRangeModified = dialog?.isSavedTimeRangeModified ?? true;
  const isSavedVariableModified = dialog?.isSavedVariableModified ?? true;
  const [saveDefaultTimeRange, setSaveDefaultTimeRange] = useState(isSavedTimeRangeModified);
  const [saveDefaultVariables, setSaveDefaultVariables] = useState(
    !enableDashboardSavedViews && isSavedVariableModified
  );

  // Saved views state
  const [savedViewAction, setSavedViewAction] = useState<SavedViewAction>(SavedViewAction.Skip);
  const { currentView } = useTemplateVariableViewState();
  const savedViews = useSavedViews();
  const existingDefaultViewName = useDefaultViewName();
  const { getSavedVariablesStatus } = useTemplateVariableActions();
  const { modifiedVariableNames } = getSavedVariablesStatus();

  // Generate auto name from only the modified variables so the suggestion
  // reflects what actually changed rather than all current variable values.
  const modifiedView = useMemo(
    () =>
      Object.fromEntries(
        modifiedVariableNames.filter((name) => name in currentView).map((name) => [name, currentView[name]!])
      ),
    [modifiedVariableNames, currentView]
  );
  const autoGeneratedName = useMemo(() => generateDefaultNameFromValues(modifiedView), [modifiedView]);
  const [defaultViewName, setDefaultViewName] = useState(existingDefaultViewName || autoGeneratedName);
  const [newViewName, setNewViewName] = useState(autoGeneratedName);

  const isDefaultViewNameDuplicate = useMemo(
    () => isDuplicateViewName(defaultViewName, savedViews),
    [defaultViewName, savedViews]
  );
  const isNewViewNameDuplicate = useMemo(
    () => isDuplicateViewName(newViewName, savedViews, existingDefaultViewName),
    [newViewName, savedViews, existingDefaultViewName]
  );

  const showSavedViewsOptions = enableDashboardSavedViews && isSavedVariableModified;

  const isOpen = dialog !== undefined;

  // Reset all form state when the dialog opens. The component stays mounted in
  // edit mode, so useState initializers only run once. Tracking the previous
  // open state lets us reset during rendering (no extra render pass from useEffect).
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setSaveDefaultTimeRange(isSavedTimeRangeModified);
      setSaveDefaultVariables(!enableDashboardSavedViews && isSavedVariableModified);
      setSavedViewAction(SavedViewAction.Skip);
      setDefaultViewName(existingDefaultViewName || autoGeneratedName);
      setNewViewName(autoGeneratedName);
    }
  }

  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const { timeRange } = useTimeRange();
  const currentTimeRangeText = `(${formatTimeRangeInputValue({ timeRange, timeFormat, timeZone })})`;
  const saveTimeRangeText = `Save current time range as new default ${currentTimeRangeText}`;

  const saveVariablesText = `Save current variable values as new default (${
    modifiedVariableNames.length > 0 ? modifiedVariableNames.join(', ') : 'No modified variables'
  })`;

  const getDefaultText = () => {
    if (showSavedViewsOptions) {
      return SAVE_CHANGES_WITH_SAVED_VIEWS_TEXT;
    }
    return enableDashboardSavedViews ? SAVE_DEFAULT_TIME_RANGE_TEXT : SAVE_DEFAULTS_DIALOG_TEXT;
  };

  const handleSaveChanges = () => {
    if (showSavedViewsOptions) {
      if (savedViewAction === SavedViewAction.UpdateDefault) {
        dialog?.onSaveChanges(saveDefaultTimeRange, true, { defaultViewName });
        analytics?.track(
          SAVED_VIEW_EVENTS.UPDATED,
          savedViewPayload(analyticsPayload, {
            viewName: defaultViewName,
            updateType: 'defaults',
            source: SAVED_VIEW_SOURCE.DIALOG,
          })
        );
      } else if (savedViewAction === SavedViewAction.CreateNew) {
        dialog?.onSaveChanges(saveDefaultTimeRange, false, { createSavedView: { name: newViewName } });
        analytics?.track(
          SAVED_VIEW_EVENTS.CREATED,
          savedViewPayload(analyticsPayload, {
            viewName: newViewName,
            isDefault: false,
            source: SAVED_VIEW_SOURCE.DIALOG,
          })
        );
      } else {
        // skip -- no variable action
        dialog?.onSaveChanges(saveDefaultTimeRange, false);
      }
    } else {
      dialog?.onSaveChanges(saveDefaultTimeRange, saveDefaultVariables);
    }
  };

  const isSaveDisabled =
    showSavedViewsOptions &&
    (() => {
      if (savedViewAction === SavedViewAction.UpdateDefault) {
        return defaultViewName.trim() === '' || isDefaultViewNameDuplicate;
      }
      if (savedViewAction === SavedViewAction.CreateNew) {
        return newViewName.trim() === '' || isNewViewNameDuplicate;
      }
      return false;
    })();

  return (
    <Modal
      open={isOpen}
      title="Save dashboard"
      onClose={dialog?.onCancel}
      onPrimaryButtonClick={handleSaveChanges}
      primaryButtonText="Save changes"
      primaryButtonDisabled={isSaveDisabled}
      size="small"
    >
      <Typography marginBottom={2}>{dialog?.description || getDefaultText()}</Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              disabled={!isSavedTimeRangeModified}
              checked={saveDefaultTimeRange && isSavedTimeRangeModified}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaveDefaultTimeRange(e.target.checked)}
            />
          }
          label={saveTimeRangeText}
        />
        {!enableDashboardSavedViews && (
          <FormControlLabel
            control={
              <Checkbox
                disabled={!isSavedVariableModified}
                checked={saveDefaultVariables && isSavedVariableModified}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaveDefaultVariables(e.target.checked)}
              />
            }
            label={saveVariablesText}
          />
        )}
      </FormGroup>
      {showSavedViewsOptions && (
        <Stack spacing={1} mt={1}>
          <Typography variant="h4" color="text.secondary">
            Changed variables
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {modifiedVariableNames.map((name) => {
              const value = currentView[name];
              return (
                <Chip
                  key={name}
                  label={
                    <span>
                      {name}=
                      <Box component="span" sx={{ fontWeight: (theme) => theme.typography.fontWeightBold }}>
                        {Array.isArray(value) ? value.join(', ') : String(value ?? '')}
                      </Box>
                    </span>
                  }
                />
              );
            })}
          </Stack>
          <RadioGroup
            value={savedViewAction}
            values={[
              { label: 'Do not save variable changes as a view', value: SavedViewAction.Skip },
              { label: 'Update default view', value: SavedViewAction.UpdateDefault },
              { label: 'Create new saved view', value: SavedViewAction.CreateNew },
            ]}
            onChange={(_, nextValue) => setSavedViewAction(nextValue as SavedViewAction)}
          />
          {savedViewAction === SavedViewAction.UpdateDefault && (
            <TextField
              fullWidth
              label="Default view name"
              value={defaultViewName}
              onChange={(e) => setDefaultViewName(e.target.value)}
              error={isDefaultViewNameDuplicate}
              helperText={isDefaultViewNameDuplicate ? 'This name is already in use by another view' : undefined}
              size="small"
            />
          )}
          {savedViewAction === SavedViewAction.CreateNew && (
            <TextField
              fullWidth
              label="View name"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              error={isNewViewNameDuplicate}
              helperText={isNewViewNameDuplicate ? 'This name is already in use by another view' : undefined}
              size="small"
            />
          )}
        </Stack>
      )}
    </Modal>
  );
};
